import React from 'react';
import { motion } from 'framer-motion';
import { useLang } from '@/context/LanguageContext';
import { Stars } from '@/components/Stars';
import { proximaText } from '@/data/catalog';
import { MapPin } from 'lucide-react';
import flexo from '@/assets/flexo.png';
import effeLabel from '@/assets/effe-label.png';

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.5 } },
};
const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const { t, lang } = useLang();
  const proxima = proximaText(lang);
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  const words = t.hero.slogan.split(' ');
  const last = words.length - 1;

  // Spanish slogan is longer; use a smaller size so it fits in two lines.
  const sloganSize = lang === 'es'
    ? 'text-5xl sm:text-6xl lg:text-7xl xl:text-[6rem]'
    : 'text-6xl sm:text-7xl lg:text-8xl xl:text-[8.5rem]';

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-[#040f0c] pt-16"
    >
      {/* ambient darker-green light beam crossing the section, swept to the right */}
      <div className="hero-ambient" aria-hidden="true" />
      {/* parallax stars over the black background */}
      <Stars count={44} seed={3} />

      {/* big flexo lamp — absolute on desktop so its light can pass behind the text */}
      <motion.img
        src={flexo}
        alt="Flexo Canillo Escènic Arts"
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        className="flexo-img cea-flicker hidden lg:block absolute left-[-4%] top-[10%] h-[86%] w-auto z-[2] pointer-events-none select-none"
      />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center min-h-[calc(100svh-4rem)]">
        {/* mobile flexo */}
        <div className="lg:hidden order-1 flex items-center justify-center pt-4">
          <img src={flexo} alt="Flexo" className="flexo-img cea-flicker max-h-[300px] w-auto object-contain" />
        </div>

        {/* spacer for desktop flexo */}
        <div className="hidden lg:block lg:col-span-5" aria-hidden="true" />

        {/* Copy */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="lg:col-span-7 order-2 py-8 lg:py-0 relative"
        >
          {/* EFFE Label — Remarkable Arts Festival seal, top-right of the title */}
          <motion.a
            variants={item}
            href="https://www.festivalfinder.eu/effe-label"
            target="_blank"
            rel="noreferrer"
            data-testid="hero-effe-label"
            aria-label="EFFE Label 2026-2027 — Remarkable Arts Festival"
            className="absolute top-0 right-0 z-20 block w-20 sm:w-24 lg:w-28 opacity-90 hover:opacity-100 transition-opacity duration-200"
          >
            <img src={effeLabel} alt="EFFE Label 2026-2027 — Remarkable Arts Festival" className="w-full h-auto select-none" />
          </motion.a>

          <motion.div variants={item} className="flex items-center gap-3 mb-5">
            <span className="text-xs font-bold tracking-[0.18em] text-[var(--cea-accent)] uppercase">
              {t.hero.edition}
            </span>
          </motion.div>

          <motion.h1
            variants={item}
            data-testid="hero-slogan"
            className={`font-display font-normal uppercase leading-[0.82] tracking-[0.01em] text-white ${sloganSize}`}
          >
            {words.map((w, i) => (
              <span key={`${w}-${i}`} className={i === last ? 'imagination-halo' : 'cea-glow'}>
                {w}{i < last ? ' ' : ''}
              </span>
            ))}
          </motion.h1>

          {/* La fecha de la edición 2026 se retiró al acabar el festival. Aquí
              queda el lugar; la fecha que se anuncia es la de la edición que
              viene, más abajo, y sale del bloque "proxima" del JSON. */}
          <motion.div variants={item} className="mt-8">
            <p className="flex items-center gap-2.5 text-lg sm:text-xl font-semibold text-white/80">
              <MapPin size={18} className="text-[var(--cea-accent)]" /> {t.hero.place}
            </p>
          </motion.div>

          <motion.div variants={item} className="mt-4">
            <span className="inline-block text-sm font-bold tracking-wide text-[var(--cea-accent)]">
              {t.hero.free}
            </span>
          </motion.div>

          {/* Fecha de la próxima edición. Sale del bloque "proxima" de
              festivalData.json; con "actiu": false no se pinta nada. */}
          {proxima && (
            <motion.div variants={item} className="mt-7">
              <div
                data-testid="hero-proxima"
                className="inline-flex items-stretch gap-4 rounded-xl border border-[var(--cea-border-strong)] bg-[#0b241b]/70 px-5 py-3.5 backdrop-blur-sm"
              >
                <span className="w-px rounded-full bg-[var(--cea-accent)] cea-glow" aria-hidden="true" />
                <span className="block">
                  <span className="block text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--cea-accent)]">
                    {proxima.etiqueta}
                  </span>
                  <span className="mt-1.5 block font-display text-xl sm:text-2xl font-normal uppercase leading-none tracking-wide text-white">
                    {proxima.dates}
                  </span>
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* animated scroll-down mouse */}
      <motion.button
        onClick={() => go('entorn')}
        aria-label="Scroll"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:block"
        data-testid="hero-scroll-indicator"
      >
        <span className="scroll-mouse block" />
      </motion.button>
    </section>
  );
};
